import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { clearNeedsYouStripHiddenOnSignIn, clearTenantSessionState } from '@lumi/app-kit';
import { userApi } from '../utils/api';
import { setStoredTenantSlug } from '../utils/runtime-config';
import { PICKED_TENANT_STORAGE_KEYS } from '../utils/pickedTenantStorage';
import { decodeJwt } from '../utils/stored-user';
import { forgetStatutoryRulesAccess } from '../utils/statutoryRulesAccess';
import { forgetTrainingOccupationsAccess } from '../utils/trainingOccupationsAccess';
import { forgetSetupEntries } from '../utils/setupEntriesCache';

export interface User {
  userId: string;
  // Null for a phone-only account (issue 698, ADR 0093): user-api omits the
  // token's `email` claim when the account has no address. Display only — the
  // session is the user id.
  email: string | null;
  isSystemAdmin: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'));

  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'));
  const user = ref<User | null>(null);

  function isTokenValid(value: string | null): value is string {
    if (!value) return false;
    const payload = decodeJwt(value);
    // A session is a valid token that names a user. The `email` claim is NOT
    // required: a phone-only account's token has none (issue 698, ADR 0093).
    if (!payload?.userId) return false;
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) return false;
    return true;
  }

  function hydrateFromToken() {
    if (!isTokenValid(token.value)) return;
    const payload = decodeJwt(token.value);
    if (payload?.userId) {
      user.value = {
        userId: payload.userId,
        email: typeof payload.email === 'string' && payload.email ? payload.email : null,
        isSystemAdmin: !!payload.isSystemAdmin,
      };
    }
  }

  function setToken(newToken: string) {
    token.value = newToken;
    localStorage.setItem('token', newToken);
    hydrateFromToken();
  }

  function setRefreshToken(newRefreshToken: string | null) {
    refreshToken.value = newRefreshToken;
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Adopt the token pair a sign-in just produced. The POST itself lives at the
   * call site (`LoginView`), inside the mutation seam: signing in is a
   * user-initiated write with a tier and a failure the form has to render, and a
   * store has no setup context to run the seam in (issue 195, decision D5).
   */
  function adoptSession(session: { token: string; refreshToken?: string | null }) {
    setToken(session.token);
    if (session.refreshToken) {
      setRefreshToken(session.refreshToken);
    }
    // A fresh login ends the needs-you strip's "Ausblenden" for this account,
    // however the previous login ended (issue 713). An SSO ticket redeemed here
    // counts as a sign-in too, the landing checkout's handoff included; a token
    // refresh and `setToken` (a module app receiving the token) never do.
    clearNeedsYouStripHiddenOnSignIn(user.value?.userId);
  }

  function logout() {
    // Best-effort server-side revocation of the refresh token (ADR 0024 rule 13);
    // fire-and-forget so logout stays synchronous and never blocks on the network.
    // Session transport, not a user-initiated write: it runs with no surface to
    // confirm or fail into, so no D4 tier can honestly be chosen for it.
    const rt = refreshToken.value;
    // seam-exempt: transport — fire-and-forget refresh-token revocation (ADR 0024 rule 13).
    if (rt) void userApi.post('/auth/logout', { refreshToken: rt }).catch(() => {});
    token.value = null;
    refreshToken.value = null;
    user.value = null;
    // The tenant scope is dropped through its own setter, not through the shared
    // key list below: `kb_tenant_slug` has a reactive mirror (see runtime-config)
    // and a bare removeItem would leave every computed reading it on the old value.
    setStoredTenantSlug('');
    // One sign-out clears the whole tenant origin, not just this app's token
    // (issue 195, D16 / addendum A13): the shared active-role + delegation keys are
    // written by every module UI on this origin, and a stale one costs the next
    // session a forced role re-pick.
    // The remembered tenant pick (#364 F9b) is this app's own key pair, so it goes
    // in the caller-supplied list. It is owner-stamped and would be refused for the
    // next account anyway; dropping it here means it is not even left lying around.
    clearTenantSessionState(['token', 'refreshToken', ...PICKED_TENANT_STORAGE_KEYS]);
    // What time-api said this account may do with the statutory rules is the
    // account's: the next one signing in on this tab asks for itself.
    forgetStatutoryRulesAccess();
    // Likewise what apprentice-api said about the training occupations (ADR 0092).
    forgetTrainingOccupationsAccess();
    forgetSetupEntries();
  }

  /**
   * A SESSION, not a leftover key (issue 364, F13).
   *
   * This used to return true whenever a `refreshToken` string existed in
   * localStorage — a presence check that was never validated and never
   * redeemed. Any stale refresh token on the tenant origin (a module UI that
   * 401-logged-out and removed only `token`, a previous account, an expired
   * one) therefore walked a signed-out visitor straight past the route guard:
   * the shell rendered, every API call 401'd, and nothing routed them to login,
   * because as far as routing was concerned they were signed in. The only thing
   * that cleared the key was an explicit sign-out, which is why "logging out" a
   * user who was not there was the way back to the login screen.
   *
   * Now only a VALID access token counts. A refresh-token-only session is not
   * refused — it is REPAIRED first: the router guard redeems it through
   * `ensureFreshSession()` before this is read, and drops the session when the
   * redemption fails, so the answer here is "yes" exactly when there is a live
   * token to show for it. Anything else fails closed to the login screen.
   */
  const isAuthenticated = computed(() => isTokenValid(token.value));

  hydrateFromToken();

  return { token, refreshToken, user, setToken, setRefreshToken, hydrateFromToken, adoptSession, logout, isAuthenticated, isTokenValid };
});
